import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Mail, ArrowLeft, Send, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setIsSubmitted(true);
      toast.success("Reset link sent! Check your email.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset link");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-split">
      {/* Left Side - Image */}
      <div 
        className="auth-image relative hidden md:flex items-center justify-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1710020319182-3102611ed86b?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#663399]/80"></div>
        <div className="relative z-10 text-white p-12 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img src="/images/logo.png" alt="Future Owners Logo" className="w-16 h-16 object-contain" />
            <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Future Owners</span>
          </div>
          <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Reset Your Password
          </h2>
          <p className="text-lg text-white/80">
            Don't worry, we'll help you get back into your account securely.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex items-center justify-center p-8 bg-gradient-to-b from-white to-secondary/20">
        <Card className="w-full max-w-md border-border/50 shadow-soft" data-testid="forgot-password-card">
          <CardHeader className="space-y-1 text-center pb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/images/logo.png" alt="Future Owners Logo" className="w-12 h-12 object-contain" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>Future Owners</span>
            </div>
            <CardTitle className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Forgot Password?
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {isSubmitted 
                ? "Check your email for the reset link"
                : "Enter your email and we'll send you a reset link"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isSubmitted ? (
              <div className="text-center space-y-6">
                <div className="flex justify-center">
                  <div className="rounded-full bg-green-100 p-4">
                    <CheckCircle className="h-12 w-12 text-green-600" />
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    We've sent a password reset link to <strong>{email}</strong>.
                    The link will expire in 24 hours.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Didn't receive the email? Check your spam folder or try again.
                  </p>
                </div>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setIsSubmitted(false)}
                    data-testid="try-again-button"
                  >
                    Try Another Email
                  </Button>
                  <Link to="/login">
                    <Button
                      variant="ghost"
                      className="w-full"
                      data-testid="back-to-login-button"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Login
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 rounded-lg border-border/80 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      required
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                  disabled={isLoading}
                  data-testid="send-reset-link-button"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Sending...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>Send Reset Link</span>
                      <Send className="h-5 w-5" />
                    </div>
                  )}
                </Button>
                <div className="text-center">
                  <Link 
                    to="/login" 
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    data-testid="back-to-login-link"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
